import {
  ChangeEvent,
  forwardRef,
  useState,
  useRef,
  useEffect,
  MutableRefObject,
} from "react";
import classNames from "classnames";

interface Country {
  code: string;
  flag: string;
  name: string;
  dialCode: string;
}

const countries: Country[] = [
  { code: "NG", flag: "🇳🇬", name: "Nigeria", dialCode: "+234" },
  { code: "US", flag: "🇺🇸", name: "United States", dialCode: "+1" },
  { code: "GB", flag: "🇬🇧", name: "United Kingdom", dialCode: "+44" },
  { code: "CA", flag: "🇨🇦", name: "Canada", dialCode: "+1" },
  { code: "IN", flag: "🇮🇳", name: "India", dialCode: "+91" },
  { code: "DE", flag: "🇩🇪", name: "Germany", dialCode: "+49" },
  { code: "FR", flag: "🇫🇷", name: "France", dialCode: "+33" },
  { code: "AU", flag: "🇦🇺", name: "Australia", dialCode: "+61" },
  { code: "BR", flag: "🇧🇷", name: "Brazil", dialCode: "+55" },
  { code: "ZA", flag: "🇿🇦", name: "South Africa", dialCode: "+27" },
];

interface PhoneNumberInputProps {
  label?: string;
  placeholder?: string;
  className?: string;
  onChange?: (phoneNumber: string, countryCode: string) => void;
  name?: string;
  value?: string;
  error?: string;
  readOnly?: boolean;
  variant?: "primary" | "secondary";
  autoComplete?: string;
  defaultCountry?: string;
}

const PhoneNumberInput = forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  (
    {
      label,
      placeholder = "Phone number",
      className,
      onChange,
      name,
      value,
      error,
      readOnly = false,
      variant = "primary",
      autoComplete = "tel",
      defaultCountry = "NG",
    },
    ref
  ) => {
    // Find default country or use first in list
    const defaultCountryObj =
      countries.find((c) => c.code === defaultCountry) || countries[0];

    const [selectedCountry, setSelectedCountry] =
      useState<Country>(defaultCountryObj);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Handle clicks outside the dropdown to close it
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setShowDropdown(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handle initial value
    useEffect(() => {
      if (value) {
        // Try to extract country code and number
        for (const country of countries) {
          if (value.startsWith(country.dialCode)) {
            setSelectedCountry(country);
            const numberPart = value.substring(country.dialCode.length).trim();
            setPhoneNumber(formatPhoneNumber(numberPart.replace(/\D/g, "")));
            break;
          }
        }
      }
    }, [value]);

    // Handle phone number changes
    const handlePhoneNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
      let inputValue = e.target.value;

      // Remove the country code prefix if present
      if (inputValue.startsWith(selectedCountry.dialCode)) {
        inputValue = inputValue
          .substring(selectedCountry.dialCode.length)
          .trim();
      }

      // Remove non-digits
      const digitsOnly = inputValue.replace(/\D/g, "");
      const formattedValue = formatPhoneNumber(digitsOnly);

      setPhoneNumber(formattedValue);

      if (onChange) {
        onChange(
          `${selectedCountry.dialCode}${digitsOnly}`,
          selectedCountry.code
        );
      }
    };

    // Format phone number based on the selected country
    const formatPhoneNumber = (value: string): string => {
      if (selectedCountry.code === "US" || selectedCountry.code === "CA") {
        // Format for US/Canada: (123) 456-7890
        const match = value.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
        if (match) {
          return !match[2]
            ? match[1]
            : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ""}`;
        }
      } else if (selectedCountry.code === "NG") {
        // Format for Nigeria: 000 000 0000
        const match = value.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
        if (match) {
          return `${match[1]}${match[2] ? ` ${match[2]}` : ""}${
            match[3] ? ` ${match[3]}` : ""
          }`;
        }
      }
      // Default formatting
      return value;
    };

    // Handle country selection
    const handleCountrySelect = (country: Country) => {
      setSelectedCountry(country);
      setShowDropdown(false);

      // Focus back on input after selecting country
      if (inputRef.current) {
        inputRef.current.focus();
      }

      if (onChange && phoneNumber) {
        onChange(
          `${country.dialCode}${phoneNumber.replace(/\D/g, "")}`,
          country.code
        );
      }
    };

    // Combine refs
    const combinedRef = (element: HTMLInputElement | null) => {
      if (element) {
        // Set the reference to inputRef
        // inputRef.current = element;

        // Forward the ref
        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          (ref as MutableRefObject<HTMLInputElement | null>).current = element;
        }
      }
    };

    return (
      <div className={`flex flex-col gap-2 md:gap-1 ${className}`}>
        {label && <p className="font-medium">{label}</p>}

        <div className="relative w-full flex items-center">
          {/* Country selector */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className={classNames(
                "flex items-center justify-center h-[40px] w-[80px] rounded-l-full border border-[#9D9898] border-r-0 focus:outline-none",
                {
                  "bg-transparent": variant === "secondary",
                  "bg-transparent border-[1px] primary-inputSd solid":
                    variant === "primary",
                  "border-[#FF0101]": error,
                }
              )}
              onClick={() => setShowDropdown(!showDropdown)}
              disabled={readOnly}
            >
              <div className="flex items-center">
                <span className="mr-1 text-base">{selectedCountry.flag}</span>
                <span className="text-xs">{selectedCountry.dialCode}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 ml-1 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </button>

            {/* Country dropdown */}
            {showDropdown && (
              <div className="absolute z-10 mt-1 w-56 max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {countries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleCountrySelect(country)}
                  >
                    <span className="mr-2 text-base">{country.flag}</span>
                    <span className="mr-2">{country.name}</span>
                    <span className="ml-auto text-gray-500">
                      {country.dialCode}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Phone number input */}
          <input
            ref={combinedRef}
            id={name}
            placeholder={placeholder}
            className={classNames(
              "text-field flex-1 h-[40px] border rounded-r-full border-[#9D9898] focus:shadow-[#3B82F640]/25 p-4 outline-none text-[12px] md:text-[14px]",
              {
                "bg-transparent": variant === "secondary",
                "bg-transparent border-[1px] primary-inputSd solid":
                  variant === "primary",
                "border-[#FF0101]": error,
              }
            )}
            onChange={handlePhoneNumberChange}
            value={phoneNumber}
            type="tel"
            name={name}
            readOnly={readOnly}
            autoComplete={autoComplete}
          />
        </div>

        {error && (
          <span className="error-text text-[#FF0101] text-[12px]">{error}</span>
        )}
      </div>
    );
  }
);

PhoneNumberInput.displayName = "PhoneNumberInput";

export default PhoneNumberInput;
